import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Loader from "../component/Loader";

export default function CartPage() {
  const [cartItems, setCartItems] = useState([]);
  const [orderSummary, setOrderSummary] = useState(null);

  const fetchCartData = () => {
    let items = [];
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      try {
        const rawItem = localStorage.getItem(key);
        if (rawItem) {
          const item = JSON.parse(rawItem);
          if (item?.id && item?.name) {
            items.push(item);
          }
        }
      } catch (err) {
        // ignore parsing errors for non-cart keys
      }
    });

    setCartItems(items);
    calculateOrderSummary(items);
  };

  useEffect(() => {
    fetchCartData();
  }, []);

  const calculateOrderSummary = (items) => {
    if (items.length === 0) {
      setOrderSummary({ subtotal: 0 });
      localStorage.removeItem("order");
      return;
    }

    const subtotal = items.reduce(
      (acc, item) => acc + item.price * item.quantity,
      0
    );
    const order = { subtotal };
    localStorage.setItem("order", JSON.stringify(order));
    setOrderSummary(order);
  };

  const updateQuantity = (id, increment) => {
    const updatedCart = cartItems.map((item) => {
      if (item.id === id) {
        const newQuantity = increment
          ? Math.min(item.quantity + 1, 10)
          : Math.max(item.quantity - 1, 1);
        localStorage.setItem(
          id,
          JSON.stringify({ ...item, quantity: newQuantity })
        );
        window.dispatchEvent(new Event("cartUpdated"));
        return { ...item, quantity: newQuantity };
      }
      return item;
    });
    setCartItems(updatedCart);
    calculateOrderSummary(updatedCart);
  };

  const removeItem = (id) => {
    localStorage.removeItem(id);
    const updatedCart = cartItems.filter((item) => item.id !== id);
    setCartItems(updatedCart);
    calculateOrderSummary(updatedCart);
    window.dispatchEvent(new Event("cartUpdated"));
  };

  if (orderSummary === null) {
    return <Loader />;
  }

  return (
    <div className="bg-black">
      <div className="bg-black text-white font-outfit pt-20 pb-20 mt-40">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-10">
          <div className="flex-1">
            <h1 className="text-4xl md:text-5xl font-syne font-bold mb-10 text-center md:text-left">
              Your Cart{" "}
              <span className="text-gray-400">({cartItems.length})</span>
            </h1>

            {cartItems.length === 0 ? (
              <div className="text-center py-20">
                <div className="not_found_wrp">Your cart is empty.</div>
                <Link
                  to="/shop"
                  className="w-full sm:w-fit bg-gray-800 hover:bg-gray-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors mt-[15px] block sm:inline-block"
                >
                  Continue Shopping
                </Link>
              </div>
            ) : (
              <div className="space-y-8">
                {cartItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex flex-col md:flex-row items-center justify-between p-6 bg-gray-900 rounded-xl shadow-lg"
                  >
                    <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-6 w-full md:w-auto">
                      <img
                        src={`${process.env.REACT_APP_SITE_IMAGES}${item.photo}`}
                        alt={item.name}
                        className="object-contain w-[70px] h-[100px] bg-brandBlue p-2 rounded"
                      />
                      <div className="text-center md:text-left">
                        <h2 className="font-syne text-xl md:text-2xl font-bold mb-1">
                          {item.name}
                        </h2>
                        <p className="cartshort_desc">
                          {item.short_desc}
                        </p>
                        <p className="text-lg font-bold mt-2 text-blue-400">
                          ${item.price.toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-6 w-full md:w-auto">
                      <div className="text-center md:text-left">
                        <h2 className="font-syne text-xl md:text-2xl font-bold mb-[30px]">
                          Volume Range
                        </h2>
                        <p className="text-lg font-bold mt-2 text-blue-400">
                          {item.volume}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-6 mt-6 md:mt-0">
                      <div className="flex items-center border border-gray-700 rounded-full">
                        <button
                          className="text-2xl px-3 py-1 text-gray-400 hover:text-white transition-colors"
                          onClick={() => updateQuantity(item.id, false)}
                        >
                          −
                        </button>
                        <span className="px-3 text-lg font-semibold">
                          {item.quantity}
                        </span>
                        <button
                          className="text-2xl px-3 py-1 text-gray-400 hover:text-white transition-colors"
                          onClick={() => updateQuantity(item.id, true)}
                        >
                          +
                        </button>
                      </div>
                      <div className="font-bold text-lg md:text-xl">
                        ${(item.price * item.quantity).toFixed(2)}
                      </div>
                      <button
                        className="text-sm text-red-500 hover:text-red-400 underline transition-colors hidden md:block"
                        onClick={() => removeItem(item.id)}
                      >
                        Remove
                      </button>
                    </div>
                    <button
                      className="text-sm text-red-500 hover:text-red-400 underline mt-4 md:hidden"
                      onClick={() => removeItem(item.id)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
            {
              cartItems.length > 0 && (
                <div className="flex mt-4 justify-end">
                  <Link
                    to="/shop"
                    className="w-fit bg-gray-800 hover:bg-gray-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors text-center">
                    Continue Shopping
                  </Link>
                </div>
              )
            }
          </div>
          {cartItems.length > 0 && (
            <div className="w-full md:w-1/3 bg-gray-900 p-6 rounded-xl shadow-lg h-fit mt-10 md:mt-24">
              <h2 className="text-2xl font-syne font-bold mb-6 text-center md:text-left">
                Order Summary
              </h2>
              <div className="flex justify-between text-lg font-semibold mb-4">
                <span>Subtotal</span>
                <span className="text-blue-400">
                  ${orderSummary.subtotal.toFixed(2)}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row md:flex-col space-y-4 sm:space-y-0 sm:space-x-4 md:space-x-0 md:space-y-4 mt-8">
                <Link
                  to="/checkout"
                  className="w-full sm:w-auto text-white font-bold px-8 py-3 rounded-lg shadow-lg transition-colors text-center"
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(67,170,214,1) 10%, rgba(0,0,0,1) 100%)",
                  }}
                >
                  Proceed To Checkout
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
